import styled from "@emotion/styled";
import { ExpandMore } from "@mui/icons-material";
import { Chip, Menu, MenuItem, Tooltip } from "@mui/material";
import React, { useState } from "react";
import { useSelector } from "react-redux";

const statusColors = {
    'Overdue': 'error',
    '0 Days': 'warning',
    '1 Days': 'info'
};

const ManagerDropDownItem = styled(MenuItem)(({ theme }) => ({
    textDecoration: 'none',
    color: theme.palette.text.primary,
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}))

const FocusableChip = styled(Chip)(({ theme }) => ({
    "&:focus-visible": {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: 2,
    },
}));

const StatusChip = ({ id, status, remainingDays, parentType = '', onChange = null, disabled = false, noMenu = false, responsibleId = null }) => {
    const [statusMenuAnchorEl, setStatusMenuAnchorEl] = useState(null);
    const isAwaitingAuthorization = parentType === "project" && status === "Awaiting Authorization";
    const chipStatusColor = statusColors[remainingDays] || (isAwaitingAuthorization ? "warning" : "success");
    const taskMenu = ['Pending', 'In Progress', 'Testing', 'Ready for Review', 'Approved', 'On Hold', 'Cancelled'];
    const projectMenu = ['Awaiting Authorization', 'Not Started', 'In Progress', 'On Hold', 'Cancelled'];
    const permissions = useSelector(state => state.auth.permissions);
    const currentUserId = useSelector(state => state.auth.userId);
    const hasEditPermission = permissions.includes('editTasks');
    const isResponsible = !!responsibleId && !!currentUserId && String(responsibleId) === String(currentUserId);
    const canEdit = hasEditPermission || isResponsible;

    const handleStatusMenuClick = (event) => {
        if (!disabled) {
            setStatusMenuAnchorEl(event.currentTarget);
        }
    };

    const handleStatusMenuChildClick = (event) => {
        if (!disabled) {
            const parentElement = event.currentTarget.parentElement;
            setStatusMenuAnchorEl(parentElement);
        }
    }

    const handleStatusMenuClose = () => {
        setStatusMenuAnchorEl(null);
    };

    const ChipComponent = isAwaitingAuthorization ? FocusableChip : Chip;

    const chip = (
        <ChipComponent
            color={chipStatusColor}
            size="small"
            variant="outlined"
            label={status}
            {...(isAwaitingAuthorization && { tabIndex: 0 })}
            {...(canEdit && !noMenu && onChange && !isAwaitingAuthorization && {
                deleteIcon: <ExpandMore />,
                onClick: handleStatusMenuClick,
                onDelete: handleStatusMenuChildClick
            })}
            disabled={disabled}
        />
    );

    return (
        <>
            {isAwaitingAuthorization ? (
                <Tooltip describeChild title="Waiting for authorization. The status can't be changed until the project is authorized or rejected.">
                    {chip}
                </Tooltip>
            ) : (
                chip
            )}
            {!noMenu && onChange &&
                <Menu
                    anchorEl={statusMenuAnchorEl}
                    open={Boolean(statusMenuAnchorEl) && !disabled}
                    onClose={handleStatusMenuClose}
                >
                    {parentType === 'task' &&
                        taskMenu.map((taskStatus) => (
                            <ManagerDropDownItem
                                key={taskStatus}
                                onClick={() => {
                                    onChange(id, taskStatus);
                                    handleStatusMenuClose();
                                }}
                            >
                                {taskStatus}
                            </ManagerDropDownItem>
                        ))
                    }

                    {parentType === 'project' &&
                        projectMenu.map((projectStatus) => (
                            <ManagerDropDownItem
                                key={projectStatus}
                                onClick={() => {
                                    onChange(id, projectStatus);
                                    handleStatusMenuClose();
                                }}
                            >
                                {projectStatus}
                            </ManagerDropDownItem>
                        ))
                        }

                </Menu>
            }
        </>
    );
};

export default StatusChip;